import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import { formatBackupSize, formatBackupDate } from './BackupRow';

// Mesmo padrão visual de CronStatusChip/WhatsAppPlatformStatusCard (bolinha + texto).
const STATUS_CONFIG = {
  ok: { label: 'OK', bg: '#E8F5E9', border: '#C8E6C9', dot: '#2E7D32', text: '#2E7D32' },
  late: { label: 'Atrasado', bg: '#FFF3E0', border: '#FFE0B2', dot: '#FB8C00', text: '#E65100' },
  not_configured: { label: 'Não configurado', bg: '#F5F5F5', border: '#E0E0E0', dot: '#9E9E9E', text: '#616161' },
};

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_configured;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1.25,
        py: 0.4,
        borderRadius: '20px',
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        width: 'fit-content',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{ fontWeight: 500, fontSize: '0.75rem', lineHeight: 1, color: cfg.text, whiteSpace: 'nowrap' }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

/** Linha "rótulo: valor" usada dentro dos blocos Banco/Uploads. */
function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );
}

/** Status do bloco (ok/atrasado/não configurado), igual à regra do backend (backup_status_summary). */
function blockStatus(configured, late) {
  if (!configured) return 'not_configured';
  return late ? 'late' : 'ok';
}

/**
 * RemoteBackupStatus - Status do backup fora do servidor (bucket S3 / Backblaze B2)
 * na tela Backups do Admin Geral. Mesma fonte do digest diário
 * (BackupStatusReader.php). Só leitura, sem ação de escrita.
 *
 * @param {Object} props
 * @param {Object|null} props.status - retorno de remote_status.php (backup_status_summary())
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 */
function RemoteBackupStatus({ status, isLoading, error }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: error || isLoading ? 0 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: 'rgba(90, 128, 251, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CloudQueueOutlinedIcon sx={{ color: '#5A80FB', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Backup fora do servidor</Typography>
            <Typography variant="body2" color="text.secondary">
              Cópia do banco e dos uploads em bucket S3 (Backblaze B2), fora do disco da hospedagem.
            </Typography>
          </Box>
        </Box>

        {!isLoading && !error && status && (
          <StatusChip status={status.configured ? (status.any_late ? 'late' : 'ok') : 'not_configured'} />
        )}
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error">{error}</Alert>
      )}

      {!isLoading && !error && status && !status.configured && (
        <Alert severity="info">
          Destino remoto não configurado (variáveis de ambiente do bucket ausentes). Os backups continuam sendo
          gerados localmente, mas sem cópia fora do servidor.
        </Alert>
      )}

      {!isLoading && !error && status && status.configured && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Box sx={{ border: '1px solid #E8ECF0', borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Banco</Typography>
              </Box>
              <StatusChip status={blockStatus(status.configured, status.database?.late)} />
            </Box>
            <InfoRow label="Último envio" value={formatBackupDate(status.database?.state?.last_success_at) || '—'} />
            <InfoRow label="Arquivo" value={status.database?.state?.file || '—'} />
            <InfoRow label="Tamanho" value={formatBackupSize(status.database?.state?.size)} />
            <InfoRow label="Status da última execução" value={status.database?.state?.status || '—'} />
            {status.database?.state?.last_error && (
              <Typography variant="caption" color="error.main">{status.database.state.last_error}</Typography>
            )}
          </Box>

          <Box sx={{ border: '1px solid #E8ECF0', borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderZipOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Uploads</Typography>
              </Box>
              <StatusChip status={blockStatus(status.configured, status.uploads?.late)} />
            </Box>
            <InfoRow label="Último envio" value={formatBackupDate(status.uploads?.state?.last_success_at) || '—'} />
            <InfoRow label="Arquivos no manifesto" value={status.uploads?.manifest?.total ?? 0} />
            <InfoRow label="Aguardando remoção" value={status.uploads?.manifest?.missing ?? 0} />
            <InfoRow label="Enviados na última execução" value={status.uploads?.state?.uploaded ?? 0} />
            <InfoRow label="Pendentes" value={status.uploads?.state?.pending ?? 0} />
            <InfoRow label="Falhas" value={status.uploads?.state?.failed ?? 0} />
            <InfoRow label="Status da última execução" value={status.uploads?.state?.status || '—'} />
            {status.uploads?.state?.last_error && (
              <Typography variant="caption" color="error.main">{status.uploads.state.last_error}</Typography>
            )}
          </Box>
        </Box>
      )}
    </Card>
  );
}

export default RemoteBackupStatus;
